/**
 * ResetPasswordPage.js - Public page for resetting password via token
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../services/api';
import Logo from './Logo';

function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
    },
    box: {
      background: 'white',
      borderRadius: '20px',
      padding: '3rem',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      maxWidth: '450px',
      width: '100%',
    },
    h1: {
      fontSize: '2.5rem',
      textAlign: 'center',
      marginBottom: '0.5rem',
      color: '#667eea',
      letterSpacing: '-0.02em',
    },
    h2: {
      textAlign: 'center',
      color: '#333',
      marginBottom: '2rem',
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#333',
      fontWeight: 500,
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: '1rem',
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '1rem',
      fontSize: '1.1rem',
      borderRadius: '10px',
      cursor: loading ? 'not-allowed' : 'pointer',
      fontWeight: 600,
      marginTop: '1rem',
      opacity: loading ? 0.6 : 1,
    },
    error: {
      background: '#fee',
      color: '#c33',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    successBox: {
      textAlign: 'center',
    },
    successIcon: {
      fontSize: '3rem',
      marginBottom: '1rem',
    },
    successText: {
      color: '#555',
      marginBottom: '1.5rem',
    },
    link: {
      color: '#667eea',
      fontWeight: 600,
      textDecoration: 'underline',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      fontSize: '1rem',
    },
  };

  return (
    <div style={{ ...styles.container, position: 'relative' }}>
      <button
        onClick={() => navigate('/')}
        aria-label="Back to home"
        style={{ position: 'absolute', top: '1.25rem', left: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
      >
        <Logo size="1.6rem" variant="white" />
      </button>
      <div style={styles.box}>
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}><Logo size="2.5rem" /></h1>
        <h2 style={styles.h2}>Reset Password</h2>

        {success ? (
          <div style={styles.successBox}>
            <div style={styles.successIcon}>&#10003;</div>
            <p style={styles.successText}>Your password has been reset successfully.</p>
            <button style={styles.link} onClick={() => navigate('/')}>
              Go to Login
            </button>
          </div>
        ) : (
          <>
            {error && <div style={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <input
                  style={styles.input}
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  required
                  placeholder="At least 8 characters"
                  minLength={8}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm Password</label>
                <input
                  style={styles.input}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  required
                  placeholder="Re-enter your password"
                />
              </div>

              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
